import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Search, ExternalLink, Bookmark, MapPin, Building2, Sparkles, Lock } from "lucide-react";
import { useAuth } from "../lib/AuthContext";

const EXP = ["entry", "mid", "senior"];
const WORK = ["full-time", "part-time", "remote", "hybrid", "onsite"];

export default function FindJobs() {
  const { user } = useAuth();
  const isPremium = user?.subscription === "premium" || user?.is_admin;
  const [countries, setCountries] = useState([]);
  const [form, setForm] = useState({
    country: "United States", city: "", job_title: "", industry: "",
    experience_level: "mid", work_type: "full-time",
  });
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState([]);
  const [quota, setQuota] = useState(null); // { used, limit, remaining }
  const [limitHit, setLimitHit] = useState(false);

  useEffect(() => {
    api.get("/meta/countries")
      .then((r) => setCountries((r.data?.countries || []).map((c) => c.name)))
      .catch(() => setCountries(["United States", "India", "United Arab Emirates", "United Kingdom"]));
    // Load current quota for free users
    if (!isPremium) {
      api.get("/quota").then((r) => {
        const q = r.data?.job_search;
        if (q && q.limit) setQuota(q);
        if (q && q.remaining === 0) setLimitHit(true);
      }).catch(() => {});
    }
  }, [isPremium]);

  const search = async (e) => {
    e?.preventDefault?.();
    if (!form.job_title.trim()) {
      toast.error("Enter a job title");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/jobs/search", form);
      setResults(r.data.results || []);
      if (r.data.quota) setQuota(r.data.quota);
      if (!r.data.results?.length) toast.info("No results");
    } catch (e) {
      const status = e?.response?.status;
      if (status === 402) {
        setLimitHit(true);
        setQuota({ used: 3, limit: 3, remaining: 0 });
      } else {
        toast.error(e?.response?.data?.detail || "Search failed");
      }
    } finally {
      setBusy(false);
    }
  };

  const saveJob = async (j) => {
    try {
      await api.post("/applications", {
        title: j.title, company: j.company, platform: j.platform,
        location: j.location, link: j.link, status: "Saved",
      });
      toast.success("Saved to tracker");
    } catch {
      toast.error("Couldn't save");
    }
  };

  return (
    <div className="space-y-8">
      <div className="fade-up">
        <p className="text-xs font-bold uppercase tracking-wider text-[#1E6FE6]">Job search</p>
        <h1 className="font-display font-black text-4xl tracking-tight mt-1">Find your next role</h1>
        <p className="text-slate-400 mt-2 max-w-xl">
          We&apos;ll generate direct search links to the top platforms for your country and score each against your most recent resume.
        </p>
      </div>

      {/* Quota indicator (free users only) */}
      {quota && (
        <div
          data-testid="job-quota-banner"
          className={`rounded-2xl border-2 p-4 flex items-center justify-between gap-4 fade-up-delay-1 ${
            quota.remaining === 0
              ? "border-amber-300 bg-amber-50"
              : "border-white/10 bg-white"
          }`}
        >
          <div className="flex items-center gap-3">
            <span className={`grid place-items-center w-10 h-10 rounded-2xl ${
              quota.remaining === 0 ? "bg-amber-200 text-amber-700" : "bg-[rgba(157,0,255,0.14)] text-[#80FFEA]"
            }`}>
              {quota.remaining === 0 ? <Lock size={18} /> : <Search size={18} />}
            </span>
            <div>
              <div className="text-sm font-bold text-white">
                {quota.remaining === 0
                  ? "You have used your 3 free job searches"
                  : `${quota.remaining} of ${quota.limit} free searches remaining`}
              </div>
              <div className="text-xs text-slate-400 mt-0.5">
                {quota.remaining === 0
                  ? "Upgrade to Premium for unlimited job discovery."
                  : "Only successful searches with results are counted."}
              </div>
            </div>
          </div>
          <Link
            to="/pricing"
            data-testid="job-quota-upgrade"
            className="inline-flex items-center gap-1.5 rounded-xl bg-[var(--cavilo-blue)] hover:bg-[var(--cavilo-blue-deep)] text-white px-4 py-2 text-xs font-bold btn-press whitespace-nowrap"
          >
            <Sparkles size={12} /> Take the premium plan
          </Link>
        </div>
      )}

      <form onSubmit={search} className="glass p-6 fade-up-delay-1">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <FormSelect label="Country" value={form.country} onChange={(v) => setForm({ ...form, country: v })} options={countries} testid="job-country" />
          <FormInput label="City" value={form.city} onChange={(v) => setForm({ ...form, city: v })} placeholder="e.g. Dubai" testid="job-city" />
          <FormInput label="Job title" value={form.job_title} onChange={(v) => setForm({ ...form, job_title: v })} placeholder="Product Manager" testid="job-title" />
          <FormInput label="Industry" value={form.industry} onChange={(v) => setForm({ ...form, industry: v })} placeholder="SaaS / Fintech…" testid="job-industry" />
          <FormSelect label="Experience" value={form.experience_level} onChange={(v) => setForm({ ...form, experience_level: v })} options={EXP} testid="job-exp" />
          <FormSelect label="Work type" value={form.work_type} onChange={(v) => setForm({ ...form, work_type: v })} options={WORK} testid="job-work" />
          <div className="sm:col-span-2 lg:col-span-2 flex items-end">
            <button
              data-testid="job-search-submit"
              type="submit"
              disabled={busy || limitHit}
              className="w-full rounded-xl bg-[#1E6FE6] hover:bg-[#1257C4] text-white py-3 font-bold text-sm shadow-lg shadow-indigo-500/20 transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
            >
              <Search size={16} /> {busy ? "Searching…" : limitHit ? "Free searches used" : "Search jobs"}
            </button>
          </div>
        </div>
      </form>

      {results.length > 0 && (
        <div className="space-y-4 fade-up-delay-2">
          {results.map((j) => (
            <div key={j.id} className="glass p-6 hover-lift" data-testid={`job-card-${j.platform}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="text-xs font-bold uppercase tracking-wider text-[#1E6FE6]">{j.platform}</div>
                  <div className="font-display font-bold text-xl mt-1">{j.title}</div>
                  <div className="text-sm text-slate-400 mt-1 flex items-center gap-3 flex-wrap">
                    <span className="inline-flex items-center gap-1"><Building2 size={14} />{j.company}</span>
                    <span className="inline-flex items-center gap-1"><MapPin size={14} />{j.location}</span>
                    <span>•</span>
                    <span>{j.posted_date}</span>
                  </div>
                </div>
                {j.match_score != null && (
                  <div className="shrink-0 text-center px-4 py-2 rounded-xl bg-[#E6F0FF]">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-[#1257C4]">Match</div>
                    <div className="font-display font-black text-2xl text-[#1257C4]">{j.match_score}<span className="text-base">%</span></div>
                  </div>
                )}
              </div>
              <p className="mt-3 text-sm text-slate-300 leading-relaxed">{j.snippet}</p>
              {(j.matching_skills?.length || j.missing_skills?.length) ? (
                <div className="mt-4 flex flex-wrap gap-2">
                  {(j.matching_skills || []).slice(0, 8).map((s) => (
                    <span key={`m-${s}`} className="px-2.5 py-1 rounded-full bg-[rgba(128,255,234,0.1)] text-[#80FFEA] text-xs font-semibold">✓ {s}</span>
                  ))}
                  {(j.missing_skills || []).slice(0, 5).map((s) => (
                    <span key={`x-${s}`} className="px-2.5 py-1 rounded-full bg-[rgba(255,0,127,0.1)] text-[#FF6DAE] text-xs font-semibold">+ {s}</span>
                  ))}
                </div>
              ) : null}
              <div className="mt-5 flex flex-wrap items-center gap-3">
                <a
                  data-testid={`job-view-${j.platform}`}
                  href={j.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-xl bg-[#0F172A] text-white px-4 py-2 text-sm font-semibold hover:bg-[#1e293b] transition-colors"
                >
                  View on {j.platform} <ExternalLink size={14} />
                </a>
                <button
                  data-testid={`job-save-${j.platform}`}
                  onClick={() => saveJob(j)}
                  className="inline-flex items-center gap-2 rounded-xl bg-white border-2 border-white/10 hover:border-white/15 px-4 py-2 text-sm font-semibold"
                >
                  <Bookmark size={14} /> Save
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FormInput({ label, value, onChange, placeholder, testid }) {
  return (
    <label className="block">
      <span className="text-xs font-bold uppercase tracking-wider text-slate-400">{label}</span>
      <input
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1.5 w-full rounded-xl border-2 border-white/10 focus:border-[#1E6FE6] outline-none px-3.5 py-2.5 text-sm transition-colors"
      />
    </label>
  );
}

function FormSelect({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="text-xs font-bold uppercase tracking-wider text-slate-400">{label}</span>
      <select
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 w-full rounded-xl border-2 border-white/10 focus:border-[#1E6FE6] outline-none px-3 py-2.5 text-sm bg-white transition-colors"
      >
        {options.map((o) => <option key={o} value={o}>{o[0].toUpperCase() + o.slice(1)}</option>)}
      </select>
    </label>
  );
}
